import React, { useCallback } from 'react';
import 'network-speed';
import { ACTIONTYPE } from '../AppStateProvider';

export interface SpeedReport {
  type: string | undefined;
  result: number | string;
}

export interface SpeedResult {
  speed: number | undefined;
  time: number | undefined;
}

const DOWNLOAD_COUNT = 10;
var continueTestVar: boolean = true;
var endTimeOfDownloadRequests: number | undefined;
var flagForFinalUploadRequest: boolean = false;
export async function useSpeedTest(dispatch: React.Dispatch<ACTIONTYPE>) {
  return useCallback(() => {
    return new Promise(async () => {
      try {
        const xhr = new XMLHttpRequest();
        const initialDownloadSpeedResults: SpeedResult[] = [];
        const averageDownloadSpeedResults: SpeedResult[] = [];
        var downloadTimeStart = Date.now();
        var downloadCount = 1;
        downloadSpeed(xhr, 0, initialDownloadSpeedResults, averageDownloadSpeedResults, dispatch, downloadTimeStart);
        await xhr.addEventListener('loadend', async (e) => {
          if (downloadCount === DOWNLOAD_COUNT) {
            const finalDownloadResult: SpeedResult = countAverageSpeed(averageDownloadSpeedResults, {
              speed: 0,
              time: 0,
            });
            dispatch({ type: 'download-speed-result', speed: Number(finalDownloadResult.speed!.toFixed(2)) });
            endTimeOfDownloadRequests = averageDownloadSpeedResults[averageDownloadSpeedResults.length - 1].time;
          }
          if (downloadCount < DOWNLOAD_COUNT) {
            downloadSpeed(
              xhr,
              downloadCount,
              initialDownloadSpeedResults,
              averageDownloadSpeedResults,
              dispatch,
              downloadTimeStart
            );
            downloadCount++;
          }
        });
      } catch (error: any) {
        const report: SpeedReport = { type: 'download', result: error.message };
        dispatch({ type: 'error-speed', report });
      }

      try {
        const initialUploadSpeedResults: SpeedResult[] = [];
        const averageUploadSpeedResults: SpeedResult[] = [];
        const defaultData = generateTestData(2000);
        const data = JSON.stringify({ defaultData });
        const xhr = new XMLHttpRequest();
        var uploadCount = 1;
        var uploadTimeStart = Date.now();
        uploadSpeed(xhr, 0, initialUploadSpeedResults, averageUploadSpeedResults, dispatch, data, uploadTimeStart);
        await xhr.addEventListener('loadend', (e) => {
          if (uploadCount < 20 && !flagForFinalUploadRequest) {
            uploadSpeed(
              xhr,
              uploadCount,
              initialUploadSpeedResults,
              averageUploadSpeedResults,
              dispatch,
              data,
              uploadTimeStart
            );
            uploadCount++;
          } else if (flagForFinalUploadRequest) {
            const finalDownloadResult: SpeedResult = countAverageSpeed(averageUploadSpeedResults, {
              speed: 0,
              time: 0,
            });
            dispatch({
              type: 'upload-speed-result',
              speed: Number(finalDownloadResult.speed!.toFixed(2)),
            });
            flagForFinalUploadRequest = false;
            endTimeOfDownloadRequests = undefined;
          }
        });
      } catch (error: any) {
        const report: SpeedReport = { type: 'upload', result: error.message };
        dispatch({ type: 'error-speed', report });
      }

      try {
        const resultsRTT: number[] = [];
        const xhr = new XMLHttpRequest();
        var rttCount = 1;
        getRTTResult(xhr, resultsRTT);
        await xhr.addEventListener('loadend', (e) => {
          if (rttCount < DOWNLOAD_COUNT) {
            getRTTResult(xhr, resultsRTT);
            rttCount++;
            if (rttCount === DOWNLOAD_COUNT) {
              const result = resultsRTT.reduce((accumulator, current) => {
                return accumulator + current;
              }, 0);
              const averageResultRTT = parseFloat((result / resultsRTT.length).toFixed(2));
              dispatch({ type: 'rtt-result', result: averageResultRTT });
              var jitter = countJitter(resultsRTT);
              if (isFinite(jitter)) {
                dispatch({ type: 'jitter-result', result: jitter });
              }
            }
          }
        });
      } catch (error: any) {
        const report: SpeedReport = { type: 'rtt', result: error.message };
        dispatch({ type: 'error-speed', report });
      }
    });
  }, [dispatch]);
}

export async function useSpeedTestOnDemand(dispatch: React.Dispatch<ACTIONTYPE>, continueTest: boolean) {
  return useCallback(() => {
    return new Promise(async () => {
      continueTestVar = continueTest;
      try {
        const xhr = new XMLHttpRequest();
        const initialSpeedResults: SpeedResult[] = [];
        const averageSpeedResults: SpeedResult[] = [];
        var downloadTimeStart = Date.now();
        var downloadCount = 1;
        if (continueTestVar) {
          downloadSpeed(xhr, 0, initialSpeedResults, averageSpeedResults, dispatch, downloadTimeStart);
        }
        await xhr.addEventListener('loadend', async (e) => {
          if (continueTestVar) {
            downloadSpeed(xhr, downloadCount, initialSpeedResults, averageSpeedResults, dispatch, downloadTimeStart);
            downloadCount++;
          } else {
            const finalDownloadResult: SpeedResult = countAverageSpeed(averageSpeedResults, {
              speed: 0,
              time: 0,
            });
            dispatch({ type: 'download-speed-result', speed: Number(finalDownloadResult.speed!.toFixed(2)) });
            endTimeOfDownloadRequests = averageSpeedResults[averageSpeedResults.length - 1].time;
          }
        });
      } catch (error: any) {
        const report: SpeedReport = { type: 'download', result: error.message };
        dispatch({ type: 'error-speed', report });
      }

      try {
        const initialSpeedResults: SpeedResult[] = [];
        const averageSpeedResults: SpeedResult[] = [];
        const defaultData = generateTestData(2000);
        const data = JSON.stringify({ defaultData });
        const xhr = new XMLHttpRequest();
        var uploadCount = 1;
        var uploadTimeStart = Date.now();
        if (continueTestVar) {
          uploadSpeed(xhr, 0, initialSpeedResults, averageSpeedResults, dispatch, data, uploadTimeStart);
        }
        await xhr.addEventListener('loadend', (e) => {
          if (!flagForFinalUploadRequest) {
            uploadSpeed(xhr, uploadCount, initialSpeedResults, averageSpeedResults, dispatch, data, uploadTimeStart);
            uploadCount++;
          } else {
            const finalUploadResult: SpeedResult = countAverageSpeed(averageSpeedResults, { speed: 0, time: 0 });
            dispatch({ type: 'upload-speed-result', speed: Number(finalUploadResult.speed!.toFixed(2)) });
            flagForFinalUploadRequest = false;
            endTimeOfDownloadRequests = undefined;
          }
        });
      } catch (error: any) {
        const report: SpeedReport = { type: 'upload', result: error.message };
        dispatch({ type: 'error-speed', report });
      }

      try {
        const resultsRTT: number[] = [];
        const xhr = new XMLHttpRequest();
        if (continueTestVar) {
          getRTTResult(xhr, resultsRTT);
        }
        await xhr.addEventListener('loadend', (e) => {
          if (continueTestVar) {
            getRTTResult(xhr, resultsRTT);
            const result = resultsRTT.reduce((accumulator, current) => {
              return accumulator + current;
            }, 0);
            const averageResultRTT = parseFloat((result / resultsRTT.length).toFixed(2));
            dispatch({ type: 'rtt-result', result: averageResultRTT });
            var jitter = countJitter(resultsRTT);
            if (isFinite(jitter)) {
              dispatch({ type: 'jitter-result', result: jitter });
            }
          }
        });
      } catch (error: any) {
        const report: SpeedReport = { type: 'download', result: error.message };
        dispatch({ type: 'error-speed', report });
      }
    });
  }, [dispatch, continueTest]);
}

function downloadSpeed(
  xhr: XMLHttpRequest,
  iteration: number,
  initialSpeedResults: SpeedResult[],
  averageSpeedResults: SpeedResult[],
  dispatch: React.Dispatch<ACTIONTYPE>,
  downloadTimeStart: number
) {
  var timeStart = Date.now();
  xhr.onprogress = (event) => {
    var speedResult = countSpeed(timeStart, Date.now(), event.loaded, downloadTimeStart);
    if (iteration === 0) {
      if (isFinite(speedResult.speed!)) {
        initialSpeedResults.push(speedResult);
        const result: SpeedResult = countAverageSpeed(initialSpeedResults, speedResult);
        averageSpeedResults.push(result);
        dispatch({ type: 'download-speed', speed: result });
      }
    } else if (iteration >= 1) {
      if (isFinite(speedResult.speed!)) {
        const result: SpeedResult = countAverageSpeed(averageSpeedResults, speedResult);
        averageSpeedResults.push(result);
        dispatch({ type: 'download-speed', speed: result });
      }
    }
  };
  xhr.open('GET', process.env.REACT_APP_API! + '/download?t=' + Date.now() + iteration);
  xhr.send();
}

function uploadSpeed(
  xhr: XMLHttpRequest,
  iteration: number,
  initialSpeedResults: SpeedResult[],
  averageSpeedResults: SpeedResult[],
  dispatch: React.Dispatch<ACTIONTYPE>,
  file: string,
  uploadTimeStart: number
) {
  var timeStart = Date.now();
  xhr.upload.onprogress = (event) => {
    var currentTime = Date.now();
    var speedResult = countSpeed(timeStart, currentTime, event.loaded, uploadTimeStart);
    if (iteration === 0) {
      if (isFinite(speedResult.speed!)) {
        initialSpeedResults.push(speedResult);
        const result: SpeedResult = countAverageSpeed(initialSpeedResults, speedResult);
        averageSpeedResults.push(result);
        dispatch({ type: 'upload-speed', speed: result });
      }
    } else if (iteration >= 1) {
      if (isFinite(speedResult.speed!)) {
        const result: SpeedResult = countAverageSpeed(averageSpeedResults, speedResult);
        averageSpeedResults.push(result);
        if (endTimeOfDownloadRequests === undefined || currentTime <= endTimeOfDownloadRequests) {
          dispatch({ type: 'upload-speed', speed: result });
        } else {
          if (!flagForFinalUploadRequest) {
            const additionalResult: SpeedResult = { speed: result.speed, time: endTimeOfDownloadRequests };
            dispatch({ type: 'upload-speed', speed: additionalResult });
            flagForFinalUploadRequest = true;
          }
        }
      }
    }
  };
  xhr.open('POST', process.env.REACT_APP_API! + '/upload?t=' + Date.now() + iteration, true);
  var formData = new FormData();
  formData.append('file', file);
  xhr.setRequestHeader('Content-Type', 'application/json');
  xhr.send(file);
}

function countSpeed(startTime: any, endTime: any, fileSizeInBytes: any, testTimeStart: number) {
  const duration = (endTime - startTime) / 1000;
  const bitsLoaded = fileSizeInBytes * 8;
  const bps: any = (bitsLoaded / duration).toFixed(2);
  const kbps: any = (bps / 1000).toFixed(2);
  const mbps: any = (kbps / 1000).toFixed(2);
  const result: SpeedResult = { speed: mbps, time: endTime };
  return result;
}

function countAverageSpeed(speedResults: SpeedResult[], speedResult: SpeedResult) {
  var sum = 0;
  if (speedResult.speed !== 0) {
    speedResults.push(speedResult);
  }
  for (var i = 0; i < speedResults.length; i++) {
    sum += Number(speedResults[i].speed!);
  }

  var average = sum / speedResults.length;
  const result: SpeedResult = { speed: average, time: speedResult.time };
  return result;
}

function countJitter(resultsRTT: number[]) {
  const resultsRTTdiff: number[] = [];
  for (let i = 0; i < resultsRTT.length - 1; i++) {
    var resultRTTdiff = Math.abs(resultsRTT[i] - resultsRTT[i + 1]);
    resultsRTTdiff.push(resultRTTdiff);
  }
  const result = resultsRTTdiff.reduce((accumulator, current) => {
    return accumulator + current;
  }, 0);
  return parseFloat((result / resultsRTTdiff.length).toFixed(2));
}

const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789~!@#$%^&*()_+`-=[]{}|;':,./<>?";

function generateTestData(sizeInKmb: number) {
  const iterations = sizeInKmb * 1000;
  let result = '';
  for (var index = 0; index < iterations; index++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

function getRTTResult(xhr: XMLHttpRequest, resultsRTT: number[]) {
  const currTime = Date.now();
  xhr.open('GET', process.env.REACT_APP_API! + '/empty?t=' + Date.now() + Math.random());
  xhr.send();
  var resultRTT: number | undefined = Date.now() - currTime;
  if (isFinite(resultRTT)) {
    resultsRTT.push(resultRTT);
  }
}

export async function useEmptySpeedTest(dispatch: React.Dispatch<ACTIONTYPE>) {
  return useCallback(() => {
    return new Promise(async () => {
      dispatch({ type: 'clean-speed', speed: { time: 0, speed: 0 } });
    });
  }, [dispatch]);
}
